import React from 'react';
import { Award, Users, Clock, Shield, Target, TrendingUp, Briefcase, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { aboutText, stats, team } from '../mock';
import { PartnersCarousel } from './PartnersCarousel';
import { Button } from './ui/button';

export const About = () => {
  const valueIcons = {
    'Qualität': Award,
    'Zuverlässigkeit': Clock,
    'Kompetenz': Users,
    'Kundennähe': Shield
  };

  // Nur featured Team-Mitglieder anzeigen
  const featuredTeam = team.filter(member => member.featured);

  return (
    <section id="about" className="py-16 relative overflow-hidden">
      {/* Diagonal Background Design */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#E6F7FF] to-white"></div>
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-black/5 to-transparent transform -skew-x-12"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-black to-[#00CCDD] text-white px-6 py-2 rounded-full mb-4">
            <Users className="w-4 h-4" />
            <span className="font-semibold text-sm uppercase tracking-wide">{aboutText.subtitle}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6">
            {aboutText.title}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {aboutText.description}
          </p>
        </div>

        {/* Stats - Unique Diagonal Card Layout */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12">
          {stats.map((stat, index) => {
            const rotation = index % 2 === 0 ? 'rotate-2' : '-rotate-2';
            return (
              <div
                key={`stat-${stat.label}`}
                className={`group relative ${rotation} hover:rotate-0 transition-all duration-500`}
              >
                <div className="relative bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#00CCDD]">
                  {/* Diagonal accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-black to-[#00CCDD] opacity-10 rounded-bl-3xl"></div>
                  
                  <div className="relative text-center">
                    <p className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-black to-[#00CCDD] mb-1">
                      {stat.value}
                    </p>
                    <p className="text-gray-600 font-medium text-sm">{stat.label}</p>
                  </div>

                  {/* Hover indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-black to-[#00CCDD] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {aboutText.values.map((value) => {
            const IconComponent = valueIcons[value.title];
            
            return (
              <div key={`value-${value.title}`} className="group relative">
                <div className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#00CCDD] overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-black to-[#00CCDD] opacity-0 group-hover:opacity-5 transform scale-0 group-hover:scale-100 transition-all duration-500 rotate-12"></div>
                  
                  <div className="relative">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-black to-[#00CCDD] flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-black mb-3 group-hover:text-[#00CCDD] transition-colors">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Team Preview Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-black mb-2">Einige unserer Experten</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Lernen Sie einige Mitglieder unseres erfahrenen Teams kennen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {featuredTeam.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#00CCDD]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-black to-[#00CCDD] flex items-center justify-center mb-3">
                    <Briefcase className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-black mb-1">{member.name}</h4>
                  <p className="text-sm font-semibold text-[#00CCDD] mb-2">{member.position}</p>
                  <p className="text-xs text-gray-500">{member.experience}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Link to full team page */}
          <div className="text-center">
            <Link to="/team">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-black hover:bg-black hover:text-white group"
              >
                Unser komplettes Team kennenlernen
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Partners Section - Carousel mit Pfeilen */}
        <PartnersCarousel />
      </div>
    </section>
  );
};